// DentalImplantsData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const dentalImplants: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Dental Implants in Dubai",
            "description": "A Life-Changing Solution for Missing Teeth at Elite Body Home Polyclinic",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Dental Implants in Dubai"],
        "hero": {
            "title": "Dental Implants in Dubai",
            "subtitle": "A Life-Changing Solution for Missing Teeth",
            "description": "Teeth form an essential part of your beauty & health which directly affects your confidence while conversing, relishing delectable food and general wellness. When you lose a tooth, it not only spoils the beauty of your smile but it also leads to a series of problems in your chewing, malalignment & loss of strength in nearby teeth and resorption of your jaw-bone."
        },
        "howItWorks": {
            "title": "What Are Dental Implants?",
            "description": "Dental implants are recent and until now the most scientifically approved method for replacing missing teeth. Unlike traditional dentures or bridges, which rest on the gums, implants are implanted into the jawbone just like natural teeth."
        },
        "procedureSteps": {
            "heading": "Understanding the Dental Implant Procedure — A Step by Step Guide",
            "para": "If you are a patient, knowing what to expect for the dental implant process may ease your mind. Here is a rundown of the steps involved:",
            "steps": [
                {
                    "title": "Assessment and Initial Consultation",
                    "desc": "We start with an extensive consultation and physical examination. We use advanced imaging techniques like 3D X-rays to assess the condition of your jawbone and the placement of the missing tooth or teeth.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "Placement of the Implant",
                    "desc": "We then carry out the implant procedure once the treatment plan is decided upon. We do this under local anesthesia, and make sure you are comfortable and pain-free.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Healing and Osseointegration",
                    "desc": "The implant itself needs time to bond with the bone, which is called osseointegration. Depending on the case selected the complete process can be closed in short span of time, although the whole thing can too months to complete.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Abutment and Crown/Bridge Placement",
                    "desc": "Once the implant is integrated, placed abutment and final crown/bridge.",
                    "icon": "fa-solid fa-crown"
                },
                {
                    "title": "Aftercare",
                    "desc": "Success of dental implants depends greatly on proper aftercare. It is essential that you practice good oral hygiene, come in for regular check-ups, and follow all post-surgery instructions given to you by our team.",
                    "icon": "fa-solid fa-hands-helping"
                }
            ]
        },
        "benefits": {
            "heading": "Why Choose Dental Implants?",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Aesthetic Appeal",
                    "desc": "Dental implants look and feel just like your natural teeth. Their custom crowns are color matched so that they look like they belong to your smile.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "Improved Function",
                    "desc": "Unlike dentures, which can move around or be a sore spot, dental implants are secure in the jawbone. This means that you can chew, talk and smile with confidence.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "No Damage to Adjacent Teeth",
                    "desc": "Implants are purely individual teeth similar to your natural teeth whereas crowns & bridges on the other hand requires grinding of adjacent teeth during placements.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Longevity of Duration",
                    "desc": "When cared for appropriately, dental implants can last a lifetime. Unlike bridges or dentures, which must be replaced every few years.",
                    "icon": "fa-solid fa-hourglass-end"
                },
                {
                    "title": "High Success Rate",
                    "desc": "A scientifically backed and well done dental implant can have a 97-98% success rate among healthy people.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "eligibility": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "preparationTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "results": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    question: "How Long Do Dental Implants Last?",
                    answer: "Dental implants can survive a lifetime if properly maintained.",
                },
                {
                    question: "Is the procedure painful?",
                    answer:
                        "The procedure is performed under local anaesthesia, ensuring minimal discomfort.",
                },
                {
                    question: "How long does the process take?",
                    answer: "The entire process, including healing, may take 3–6 months.",
                },
                {
                    question: "Are dental implants expensive?",
                    answer:
                        "While the upfront cost may be higher, implants offer a long-term solution, eliminating the need for frequent replacements.",
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    ar: {
        "url": "",
        "meta": {
            "title": "زراعة الأسنان في دبي",
            "description": "حل يغير حياتك بتعويض الأسنان المفقودة في عيادات ايليت بودي هوم",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "زراعة الأسنان في دبي"],
        "hero": {
            "title": "زراعة الأسنان في دبي",
            "subtitle": "حل يغير حياتك بتعويض الأسنان المفقودة",
            "description": "تُعد الأسنان جزءًا أساسيًا من جمالك وصحتك، حيث تؤثر بشكل مباشر على ثقتك عند التحدث، واستمتاعك بالطعام، ورفاهيتك العامة."
        },
        "howItWorks": {
            "title": "ما هي زراعة الأسنان؟",
            "description": "تُعد زراعة الأسنان هي الحل الأحدث والأكثر قبولًا علميًا لتعويض الأسنان التالفة والمفقودة. يتم غرس الزرعات السنية مباشرةً في عظم الفك تمامًا مثل الأسنان الطبيعية على عكس الجسور أو الأطقم التقليدية التي تستقر فوق اللثة."
        },
        "procedureSteps": {
            "heading": "إجراء زراعة الأسنان: ما هي الخطوات؟",
            "para": "يساعد معرفة الخطوات المتبعة في عملية زراعة الأسنان على تقليل القلق وزيادة الثقة في العلاج. إليك نظرة عامة على الخطوات الرئيسية:",
            "steps": [
                {
                    "title": "الاستشارة الأولية والتقييم",
                    "desc": "تبدأ رحلتك مع فحص شامل واستشارة متخصصة باستخدام تقنيات التصوير المتقدمة مثل التصوير ثلاثي الأبعاد.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "وضع الزرعة",
                    "desc": "بمجرد تحديد الخطة العلاجية، نبدأ في إجراء الزراعة تحت تأثير التخدير الموضعي لضمان راحتك التامة.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "الالتئام والاندماج العظمي",
                    "desc": "تحتاج الزرعة السنية إلى وقت للالتحام مع العظم، وهي عملية تُعرف باسم الاندماج العظمي.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "تركيب الدعامة والتاج أو الجسر",
                    "desc": "نقوم بتركيب الدعامة بعد التأكد من الاندماج التام للزرعة مع العظم، يليها تثبيت التاج السني أو الجسر.",
                    "icon": "fa-solid fa-crown"
                },
                {
                    "title": "العناية بعد الزراعة",
                    "desc": "تلعب العناية الصحيحة دورًا أساسيًا في نجاح زراعة الأسنان. فيُعد من الضروري الحفاظ على نظافة الفم.",
                    "icon": "fa-solid fa-hands-helping"
                }
            ]
        },
        "benefits": {
            "heading": "لماذا تختار زراعة الأسنان؟",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "مظهر جمالي طبيعي",
                    "desc": "تُصمم زراعة الأسنان لتبدو مثل أسنانك الطبيعية ولتشعرك بذلك، حيث يتم توفير تيجان الأسنان بلون متطابق مع أسنانك.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "تحسين وظيفة الأسنان",
                    "desc": "على عكس أطقم الأسنان التي قد تنزلق أو تسبب انزعاجًا، يتم تثبيت الأسنان بشكل آمن داخل عظم الفك.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "الحفاظ على الأسنان المجاورة",
                    "desc": "على عكس التيجان والجسور التي تتطلب برد الأسنان المجاورة لتركيبها، فإن زراعة الأسنان تُعدّ خيارًا مستقلًا.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "تجنب فقدان العظام والشيخوخة المبكرة",
                    "desc": "تحافظ زراعة الأسنان على صحة العظام واللثة المحيطة بها، مما يساعد في تجنب الشيخوخة المبكرة.",
                    "icon": "fa-solid fa-hourglass-end"
                },
                {
                    "title": "معدلات نجاح عالية",
                    "desc": "تشير الدراسات العلمية إلى أن زراعة الأسنان المُخطط لها جيدًا والمنفذة بدقة تحقق نسبة نجاح تتراوح بين 97-98%.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "eligibility": {
            "heading": "هل أنت مرشح مثالي لزراعة الأسنان؟",
            "subheading": "",
            "para": "تُعد زراعة الأسنان حلاً متطورًا وفعالًا لتعويض سن واحد أو عدة أسنان مفقودة. وبشكل عام، لا توجد موانع محددة أو قيود صارمة لإجراء زراعة الأسنان.",
            "items": []
        },
        "preparationTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "results": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "aftercareTips": {
            "heading": "العناية بعد الزراعة",
            "subheading": "",
            "para": "تلعب العناية الصحيحة دورًا أساسيًا في نجاح زراعة الأسنان. فيُعد من الضروري الحفاظ على نظافة الفم، والالتزام بالفحوصات الدورية، واتباع تعليمات الفريق الطبي لضمان نتائج طويلة الأمد.",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم من الوقت تدوم الأسنان المزروعة؟",
                    "answer": "يمكن أن تدوم الأسنان المزروعة مدى الحياة مع العناية الجيدة والمنتظمة."
                },
                {
                    "question": "هل الإجراء مؤلم؟",
                    "answer": "يتم تنفيذ الإجراء تحت تأثير التخدير الموضعي لضمان الحد الأدنى من الشعور بالألم."
                },
                {
                    "question": "كم يستغرق إجراء زراعة الأسنان؟",
                    "answer": "قد تستغرق العملية بأكملها من 3 إلى 6 أشهر، بما في ذلك فترة التعافي."
                },
                {
                    "question": "هل تكلفة زراعة الأسنان عالية؟",
                    "answer": "على الرغم من أن التكلفة الأولية قد تكون عالية، إلا أن الزراعة توفر حلاً طويل الأمد دون الحاجة إلى استبدالات متكررة مثل الحلول الأخرى."
                },
                {
                    "question": "هل يمكن لأي شخص إجراء زراعة الأسنان؟",
                    "answer": "معظم البالغين الأصحاء مؤهلون، ولكن الاستشارة الطبية ضرورية لتحديد مدى ملاءمة الإجراء لكل حالة."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    shared: {
        images: {
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/New-Service-Bannder-Image.jpg",
            beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sdfsdgs-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Holiwood-Smile-in-Dubai-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dsfsfseff.jpg"
            ],
            introductionImage: ""
        },
        product: {
            id: 15,
            title: "Dental Implants",
            price: 1500,
            description: "Dental implants are a modern and scientifically approved method for replacing missing teeth. Unlike traditional dentures or bridges, which rest on the gums, implants are implanted into the jawbone just like natural teeth."
        }
    }
};
